import type {ItemElement, ItemProps} from '@react-types/shared';
import type {ReactNode} from 'react';
import React from 'react';
import type {PartialNode} from './types';

// @ts-expect-error - 'props' is declared but its value is never read.
function Item<T>(props: ItemProps<T>): ReactNode {
  return null;
}

Item.getCollectionNode = function* getCollectionNode<T>(
  props: ItemProps<T>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  context: any,
): Generator<PartialNode<T>> {
  const {childItems, title, children} = props;

  const rendered = props.title || props.children;
  const textValue =
    props.textValue ||
    (typeof rendered === 'string' ? rendered : '') ||
    props['aria-label'] ||
    '';

  // suppressTextValueWarning is used in components like Tabs, which don't have type to select support.
  if (!textValue && !context?.suppressTextValueWarning) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        '<Item> with non-plain text contents is unsupported by type to select for accessibility. Please add a `textValue` prop.',
      );
    }
  }

  yield {
    type: 'item',
    props,
    rendered,
    textValue,
    'aria-label': props['aria-label'],
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    hasChildNodes: hasChildItems(props),
    *childNodes() {
      if (childItems) {
        // eslint-disable-next-line no-restricted-syntax
        for (const child of childItems) {
          yield {
            type: 'item',
            value: child,
          };
        }
      } else if (title) {
        const items: PartialNode<T>[] = [];
        React.Children.forEach(children, (child) => {
          items.push({
            type: 'item',
            element: child as ItemElement<T>,
          });
        });

        yield* items;
      }
    },
  };
};

function hasChildItems<T>(props: ItemProps<T>) {
  if (props.hasChildItems != null) {
    return props.hasChildItems;
  }

  if (props.childItems) {
    return true;
  }

  if (props.title && React.Children.count(props.children) > 0) {
    return true;
  }

  return false;
}

// We don't want getCollectionNode to show up in the type definition
const _Item = Item as <T>(props: ItemProps<T>) => JSX.Element;
export {_Item as Item};
